import React, { useContext, useEffect, useState } from 'react';

import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Button, Typography } from '@mui/material';
import {
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';

import { companiesRef } from '../../../api/firebase/firestore/company';
import { issuesRef } from '../../../api/firebase/firestore/issue';
import { mediasRef } from '../../../api/firebase/firestore/media';
import { getUserInfo } from '../../../api/firebase/firestore/user';
import { blue, blackButton } from '../../../constants/Colors';
import {
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper
} from '../../../constants/Location';
import {
  getValueFromSelectOption,
  getValueFromSelectOptions
} from '../../../constants/Select';
import { screenType, status } from '../../../constants/enum';
import { AuthContext } from '../../../contexts/auth';
import { throwError } from '../../../modules/error';
import CustomGuideButton from '../../uiParts/CustomGuideButton';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import MediaCard from '../../uiParts/MediaCard';
import MediaSearchCard from '../../uiParts/MediaSearchCard';
import NothingGivenList from '../../uiParts/NothingGivenList';
import Sidebar from '../../uiParts/Sidebar';

import type { SelectOption, SelectOptions } from '../../../constants/Select';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

const styles = {
  blackButton,
  card: {
    '@media screen and (max-width:470px)': {
      mt: '12px'
    },
    mt: '24px'
  },
  comparisonButton: {
    ':disabled': {
      background: '#E0E0E0'
    },
    columnGap: '8px',
    display: 'flex',
    padding: '6px 16px'
  },
  comparisonButtonIcon: {
    height: '20px',
    width: '20px'
  },
  comparisonButtonText: {
    fontSize: '14px'
  },
  contactAllButton: {
    ':focus': {
      background: blue
    },
    ':hover': {
      background: blue
    },
    '@media screen and (max-width:960px)': {
      height: '30px',
      padding: '4px 10px',
      width: '154px'
    },
    background: blue,
    boxShadow: 'none',
    height: '36px',
    width: '200px'
  },
  contactCheck: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    margin: '22px 0 0 24px'
  },
  list: {
    '@media screen and (max-width:470px)': {
      margin: '0 16px'
    },
    margin: '0 24px'
  },
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper
};

const ClientMypageMedia: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('title.client.media');
  });

  interface Media {
    mediaId: string;
    companyId: number;
    created: Date;
    updated: Date;
    status: number;
    assigneeId: number;
    name: string;
    category: number[];
    mediaType: number;
    target: number[];
    targetType: number[];
    overview: string;
    funnel: number[];
    seasonalMenuMaterialText: string;
    value: string;
    industries: Array<{
      industriesNum: number;
      industriesOther: string;
      percentage: number;
    }>;
    continuousFactors: string[];
    image: string;
    demographicsGender: {
      male: number;
      female: number;
    };
    demographicsGeneration: {
      f10: number;
      f20: number;
      f30: number;
      f40: number;
      f50: number;
      f60: number;
      f70: number;
      m10: number;
      m20: number;
      m30: number;
      m40: number;
      m50: number;
      m60: number;
      m70: number;
    };
    demographicsArea: {
      hokkaidou: number;
      touhoku: number;
      kantou: number;
      chubu: number;
      kinki: number;
      chugoku: number;
      shikoku: number;
      kyusyu: number;
      okinawa: number;
    };
    demographicsOther: string;
    budget: number;
  }

  const [hasMore, setHasMore] = useState(true);
  const [medias, setMedias] = useState<Media[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showList, setShowList] = useState<boolean>(false);
  const [searchedMedias, setSearchedMedias] = useState<Media[]>([]);
  const [searchWord, setSearchWord] = useState<string>('');
  const [searchGenre, setSearchGenre] = useState<number[]>([]);
  const [searchMediaType, setSearchMediaType] = useState<number[]>([]);
  const [searchFunnel, setSearchFunnel] = useState<number[]>([]);
  const [searchTarget, setSearchTarget] = useState<number[]>([]);
  const [searchTargetType, setSearchTargetType] = useState<number[]>([]);
  const [searchGender, setSearchGender] = useState<{
    gender: string | null | undefined;
    percentFrom: number | null | undefined;
    percentTo: number | null | undefined;
  }>({ gender: null, percentFrom: null, percentTo: null });
  const [searchGeneration, setSearchGeneration] = useState<{
    generation: string | null | undefined;
    percentFrom: number | null | undefined;
    percentTo: number | null | undefined;
  }>({ generation: null, percentFrom: null, percentTo: null });
  const [searchAccess, setSearchAccess] = useState<{
    access: number | null | undefined;
    percentFrom: number | null | undefined;
    percentTo: number | null | undefined;
  }>({ access: null, percentFrom: null, percentTo: null });
  const [searchIndustry, setSearchIndustry] = useState<number[]>([]);
  const [searchBudget, setSearchBudget] = useState<string>('');

  const handleSearchInputChange = (
    word: string | null | undefined,
    genre: SelectOption[],
    mediaType: SelectOptions[],
    funnels: SelectOption[],
    target: SelectOption[],
    targetType: SelectOption[],
    selectedGender:
      | {
          gender: string | null | undefined;
          percentFrom: number | null | undefined;
          percentTo: number | null | undefined;
        }
      | undefined,
    selectedGeneration:
      | {
          generation: string | null | undefined;
          percentFrom: number | null | undefined;
          percentTo: number | null | undefined;
        }
      | undefined,
    selectedAccess:
      | {
          access: number | null | undefined;
          percentFrom: number | null | undefined;
          percentTo: number | null | undefined;
        }
      | undefined,
    industry: SelectOption[],
    budget: string | null | undefined
  ): void => {
    if (word) {
      setSearchWord(word);
    } else {
      setSearchWord('');
    }

    if (Array.isArray(genre) && genre.length > 0) {
      setSearchGenre(getValueFromSelectOption(genre));
    } else {
      setSearchGenre([]);
    }

    if (Array.isArray(mediaType) && mediaType.length > 0) {
      setSearchMediaType(getValueFromSelectOptions(mediaType));
    } else {
      setSearchMediaType([]);
    }

    if (Array.isArray(funnels) && funnels.length > 0) {
      setSearchFunnel(getValueFromSelectOption(funnels));
    } else {
      setSearchFunnel([]);
    }

    if (Array.isArray(target) && target.length > 0) {
      setSearchTarget(getValueFromSelectOption(target));
    } else {
      setSearchTarget([]);
    }

    if (Array.isArray(targetType) && targetType.length > 0) {
      setSearchTargetType(getValueFromSelectOption(targetType));
    } else {
      setSearchTargetType([]);
    }

    if (
      selectedGender?.gender &&
      selectedGender?.percentFrom !== undefined &&
      selectedGender?.percentFrom !== null &&
      selectedGender?.percentTo !== undefined &&
      selectedGender?.percentTo !== null
    ) {
      setSearchGender(selectedGender);
    } else {
      setSearchGender({ gender: null, percentFrom: null, percentTo: null });
    }

    if (
      selectedGeneration?.generation &&
      selectedGeneration?.percentFrom !== undefined &&
      selectedGeneration?.percentFrom !== null &&
      selectedGeneration?.percentTo !== undefined &&
      selectedGeneration?.percentTo !== null
    ) {
      setSearchGeneration(selectedGeneration);
    } else {
      setSearchGeneration({
        generation: null,
        percentFrom: null,
        percentTo: null
      });
    }

    if (
      selectedAccess?.access &&
      selectedAccess?.percentFrom !== undefined &&
      selectedAccess?.percentFrom !== null &&
      selectedAccess?.percentTo !== undefined &&
      selectedAccess?.percentTo !== null
    ) {
      setSearchAccess(selectedAccess);
    } else {
      setSearchAccess({ access: null, percentFrom: null, percentTo: null });
    }

    if (Array.isArray(industry) && industry.length > 0) {
      setSearchIndustry(getValueFromSelectOption(industry));
    } else {
      setSearchIndustry([]);
    }

    if (budget) {
      setSearchBudget(budget);
    } else {
      setSearchBudget('');
    }
  };

  useEffect(() => {
    let filteredMedias: Media[] = medias;

    if (searchWord) {
      filteredMedias = filteredMedias.filter(
        (media) =>
          media.name?.includes(searchWord) ||
          media.overview?.includes(searchWord) ||
          media.demographicsOther?.includes(searchWord)
      );
    }

    if (searchGenre.length > 0) {
      filteredMedias = filteredMedias.filter((media) =>
        searchGenre.some((prevGenre) =>
          (Array.isArray(media.category)
            ? media.category
            : [media.category]
          ).includes(prevGenre)
        )
      );
    }

    if (searchMediaType.length > 0) {
      filteredMedias = filteredMedias.filter((media) =>
        searchMediaType.some((prevMediaType) =>
          (Array.isArray(media.mediaType)
            ? media.mediaType
            : [media.mediaType]
          ).includes(prevMediaType)
        )
      );
    }

    if (searchFunnel.length > 0) {
      filteredMedias = filteredMedias.filter((media) =>
        searchFunnel.some((prevFunnel) =>
          (Array.isArray(media.funnel)
            ? media.funnel
            : [media.funnel]
          ).includes(prevFunnel)
        )
      );
    }

    if (searchTarget.length > 0) {
      filteredMedias = filteredMedias.filter((media) =>
        searchTarget.some((prevTarget) =>
          (Array.isArray(media.target)
            ? media.target
            : [media.target]
          ).includes(prevTarget)
        )
      );
    }

    if (searchTargetType.length > 0) {
      filteredMedias = filteredMedias.filter((media) =>
        searchTargetType.some((prevTargetType) =>
          (Array.isArray(media.targetType)
            ? media.targetType
            : [media.targetType]
          ).includes(prevTargetType)
        )
      );
    }

    if (
      searchGender?.gender &&
      searchGender?.percentFrom !== undefined &&
      searchGender?.percentFrom !== null &&
      searchGender?.percentTo !== undefined &&
      searchGender?.percentTo !== null
    ) {
      filteredMedias = filteredMedias.filter((media) => {
        if (media.demographicsGender) {
          if (searchGender.gender === 'male') {
            return comparePercent(
              media.demographicsGender.male,
              searchGender.percentFrom,
              searchGender.percentTo
            );
          } else if (searchGender.gender === 'female') {
            return comparePercent(
              media.demographicsGender.female,
              searchGender.percentFrom,
              searchGender.percentTo
            );
          }
        }
        return false;
      });
    }

    if (
      searchGeneration?.generation &&
      searchGeneration?.percentFrom !== undefined &&
      searchGeneration?.percentFrom !== null &&
      searchGeneration?.percentTo !== undefined &&
      searchGeneration?.percentTo !== null
    ) {
      filteredMedias = filteredMedias.filter((media) => {
        if (media.demographicsGeneration) {
          if (searchGeneration.generation === 'f10') {
            return comparePercent(
              media.demographicsGeneration?.f10,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'f20') {
            return comparePercent(
              media.demographicsGeneration?.f20,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'f30') {
            return comparePercent(
              media.demographicsGeneration?.f30,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'f40') {
            return comparePercent(
              media.demographicsGeneration?.f40,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'f50') {
            return comparePercent(
              media.demographicsGeneration?.f50,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'f60') {
            return comparePercent(
              media.demographicsGeneration?.f60,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'f70') {
            return comparePercent(
              media.demographicsGeneration?.f70,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'm10') {
            return comparePercent(
              media.demographicsGeneration?.m10,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'm20') {
            return comparePercent(
              media.demographicsGeneration?.m20,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'm30') {
            return comparePercent(
              media.demographicsGeneration?.m30,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'm40') {
            return comparePercent(
              media.demographicsGeneration?.m40,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'm50') {
            return comparePercent(
              media.demographicsGeneration?.m50,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'm60') {
            return comparePercent(
              media.demographicsGeneration?.m60,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'm70') {
            return comparePercent(
              media.demographicsGeneration?.m70,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          }
        }
        return false;
      });
    }

    if (
      searchAccess?.access &&
      searchAccess?.percentFrom !== undefined &&
      searchAccess?.percentFrom !== null &&
      searchAccess?.percentTo !== undefined &&
      searchAccess?.percentTo !== null
    ) {
      filteredMedias = filteredMedias.filter((media) => {
        if (media.demographicsArea) {
          if (searchAccess.access === 1) {
            return comparePercent(
              media.demographicsArea.hokkaidou,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          } else if (searchAccess.access === 2) {
            return comparePercent(
              media.demographicsArea.touhoku,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          } else if (searchAccess.access === 3) {
            return comparePercent(
              media.demographicsArea.kantou,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          } else if (searchAccess.access === 4) {
            return comparePercent(
              media.demographicsArea.chubu,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          } else if (searchAccess.access === 5) {
            return comparePercent(
              media.demographicsArea.kinki,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          } else if (searchAccess.access === 6) {
            return comparePercent(
              media.demographicsArea.chugoku,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          } else if (searchAccess.access === 7) {
            return comparePercent(
              media.demographicsArea.shikoku,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          } else if (searchAccess.access === 8) {
            return comparePercent(
              media.demographicsArea.kyusyu,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          } else if (searchAccess.access === 9) {
            return comparePercent(
              media.demographicsArea.okinawa,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          }
        }
        return false;
      });
    }

    if (searchIndustry.length > 0) {
      filteredMedias = filteredMedias.filter((media) => {
        if (!media.industries) return false;
        const industriesNumArray = media.industries?.map(
          (industry) => industry.industriesNum
        );
        return searchIndustry.some((industry) =>
          industriesNumArray.includes(industry)
        );
      });
    }
    if (searchBudget) {
      filteredMedias = filteredMedias.filter(
        (media) => Number(searchBudget) === media.budget
      );
    }

    // 重複を除外
    filteredMedias = filteredMedias.filter(
      (media, index, self) =>
        index === self.findIndex((m) => m.mediaId === media.mediaId)
    );

    setSearchedMedias(filteredMedias);
  }, [
    medias,
    searchWord,
    searchGenre,
    searchMediaType,
    searchFunnel,
    searchTarget,
    searchTargetType,
    searchGender,
    searchGeneration,
    searchAccess,
    searchBudget
  ]);

  const comparePercent = (
    percent: number,
    percentFrom: number | null | undefined,
    percentTo: number | null | undefined
  ): boolean => {
    if (
      percentFrom !== null &&
      percentFrom !== undefined &&
      percentTo !== null &&
      percentTo !== undefined &&
      percentFrom <= percentTo
    ) {
      return percentFrom <= percent && percent <= percentTo;
    }
    return false;
  };

  const appendQuestions = async (
    snapshot: Array<QueryDocumentSnapshot<DocumentData>>
  ): Promise<void> => {
    const companies = query(
      companiesRef,
      where('status', '==', status.approved)
    );

    const companiesSnaps = await getDocs(companies);

    let activeCompanyIds: string[] = [];
    companiesSnaps.forEach((companySnap) => {
      if (companySnap.data().status === status.approved) {
        activeCompanyIds.push(companySnap.data().companyId as string);
      }
    });

    activeCompanyIds = activeCompanyIds.filter((item) => item !== undefined);

    const gotMedias: Media[] = snapshot.map((document) => {
      const media = document.data() as Media;
      return media;
    });

    const filteredMedias = gotMedias.filter((media) =>
      activeCompanyIds.includes(`${media.companyId}`)
    );

    setSearchedMedias((prevMedias) => {
      let result = prevMedias.concat(filteredMedias);
      // もし検索ワードがあれば、検索ワードで再度フィルタリング
      if (searchWord) {
        result = result.filter(
          (media) =>
            media.name.includes(searchWord) ||
            media.overview.includes(searchWord) ||
            media.demographicsOther.includes(searchWord)
        );
      }

      if (searchGenre.length > 0) {
        result = result.filter((media) =>
          searchGenre.some((prevGenre) =>
            (Array.isArray(media.category)
              ? media.category
              : [media.category]
            ).includes(prevGenre)
          )
        );
      }

      if (searchMediaType.length > 0) {
        result = result.filter((media) =>
          searchMediaType.some((prevMediaType) =>
            (Array.isArray(media.mediaType)
              ? media.mediaType
              : [media.mediaType]
            ).includes(prevMediaType)
          )
        );
      }

      if (searchFunnel.length > 0) {
        result = result.filter((media) =>
          searchFunnel.some((prevFunnel) =>
            (Array.isArray(media.funnel)
              ? media.funnel
              : [media.funnel]
            ).includes(prevFunnel)
          )
        );
      }

      if (searchTarget.length > 0) {
        result = result.filter((media) =>
          searchTarget.some((prevTarget) =>
            (Array.isArray(media.target)
              ? media.target
              : [media.target]
            ).includes(prevTarget)
          )
        );
      }

      if (searchTargetType.length > 0) {
        result = result.filter((media) =>
          searchTargetType.some((prevTargetType) =>
            (Array.isArray(media.targetType)
              ? media.targetType
              : [media.targetType]
            ).includes(prevTargetType)
          )
        );
      }

      if (
        searchGender?.gender &&
        (searchGender?.percentFrom ?? searchGender?.percentTo)
      ) {
        result = result.filter((media) => {
          if (searchGender.gender === 'male') {
            return comparePercent(
              media.demographicsGender.male,
              searchGender.percentFrom,
              searchGender.percentTo
            );
          } else if (searchGender.gender === 'female') {
            return comparePercent(
              media.demographicsGender.female,
              searchGender.percentFrom,
              searchGender.percentTo
            );
          }
          return false;
        });
      }

      if (
        searchGeneration?.generation &&
        (searchGeneration?.percentFrom ?? searchGeneration?.percentTo)
      ) {
        result = result.filter((media) => {
          if (searchGeneration.generation === 'f10') {
            return comparePercent(
              media.demographicsGeneration.f10,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'f20') {
            return comparePercent(
              media.demographicsGeneration.f20,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'f30') {
            return comparePercent(
              media.demographicsGeneration.f30,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'f40') {
            return comparePercent(
              media.demographicsGeneration.f40,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'f50') {
            return comparePercent(
              media.demographicsGeneration.f50,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'f60') {
            return comparePercent(
              media.demographicsGeneration.f60,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'f70') {
            return comparePercent(
              media.demographicsGeneration.f70,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'm10') {
            return comparePercent(
              media.demographicsGeneration.m10,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'm20') {
            return comparePercent(
              media.demographicsGeneration.m20,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'm30') {
            return comparePercent(
              media.demographicsGeneration.m30,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'm40') {
            return comparePercent(
              media.demographicsGeneration.m40,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'm50') {
            return comparePercent(
              media.demographicsGeneration.m50,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'm60') {
            return comparePercent(
              media.demographicsGeneration.m60,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          } else if (searchGeneration.generation === 'm70') {
            return comparePercent(
              media.demographicsGeneration.m70,
              searchGeneration.percentFrom,
              searchGeneration.percentTo
            );
          }
          return false;
        });
      }

      if (
        searchAccess?.access &&
        (searchAccess?.percentFrom ?? searchAccess?.percentTo)
      ) {
        result = result.filter((media) => {
          if (searchAccess.access === 1) {
            return comparePercent(
              media.demographicsArea.hokkaidou,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          } else if (searchAccess.access === 2) {
            return comparePercent(
              media.demographicsArea.touhoku,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          } else if (searchAccess.access === 3) {
            return comparePercent(
              media.demographicsArea.kantou,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          } else if (searchAccess.access === 4) {
            return comparePercent(
              media.demographicsArea.chubu,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          } else if (searchAccess.access === 5) {
            return comparePercent(
              media.demographicsArea.kinki,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          } else if (searchAccess.access === 6) {
            return comparePercent(
              media.demographicsArea.chugoku,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          } else if (searchAccess.access === 7) {
            return comparePercent(
              media.demographicsArea.shikoku,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          } else if (searchAccess.access === 8) {
            return comparePercent(
              media.demographicsArea.kyusyu,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          } else if (searchAccess.access === 9) {
            return comparePercent(
              media.demographicsArea.okinawa,
              searchAccess.percentFrom,
              searchAccess.percentTo
            );
          }
          return false;
        });
      }

      if (searchIndustry.length > 0) {
        result = result.filter((media) => {
          const industriesNumArray = media.industries.map(
            (industry) => industry.industriesNum
          );
          return searchIndustry.some((industry) =>
            industriesNumArray.includes(industry)
          );
        });
      }

      if (searchBudget) {
        result = result.filter(
          (media) => Number(searchBudget) === media.budget
        );
      }
      // 重複を除外
      result = result.filter(
        (media, index, self) =>
          index === self.findIndex((m) => m.mediaId === media.mediaId)
      );
      return result;
    });

    setMedias((prevMedias) => prevMedias.concat(filteredMedias));
    setSearchedMedias((prevMedias) => prevMedias.concat(filteredMedias));
  };

  const loadMore = async (): Promise<void> => {
    const mediasLength = medias.length;

    const lastQuestion = medias[mediasLength - 1];

    const data = !mediasLength
      ? query(
          mediasRef,
          where('status', '==', status.approved),
          orderBy('created', 'desc'),
          limit(50)
        )
      : query(
          mediasRef,
          where('status', '==', status.approved),
          orderBy('created', 'desc'),
          startAfter(lastQuestion.created),
          limit(50)
        );

    const { docs, empty } = await getDocs(data);

    if (empty) {
      setHasMore(false);
      return;
    }
    await appendQuestions(docs);
  };

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        await loadMore();
      } catch (e) {
        throwError(e);
      } finally {
        setShowList(true);
        setLoading(false);
      }
    })();
  }, []);

  // メディア比較
  const [compareMedias, setCompareMedias] = useState<string[]>([]);
  const [compareButtonDisabled, setCompareButtonDisabled] =
    useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');

  const handleCheckboxChange = (id: string): void => {
    setCompareMedias((prev) => {
      const index = prev.indexOf(id);
      if (index === -1) {
        return [...prev, id];
      }
      return prev.filter((prevId) => prevId !== id);
    });
  };

  useEffect(() => {
    setCompareButtonDisabled(
      compareMedias.length < 1 || compareMedias.length > 10
    );
    if (compareMedias.length > 10) {
      setErrorText(t('media.compareError'));
      setOpen(true);
    }
  }, [compareMedias]);

  const { currentUser } = useContext(AuthContext);
  const [cantInquiry, setCantInquiry] = useState<string | undefined>(undefined);

  useEffect(() => {
    void (async () => {
      if (currentUser) {
        const userData = await getUserInfo(currentUser.uid);
        const comId = userData?.companyId as string;
        const comRef = doc(companiesRef, comId);
        const comSnap = await getDoc(comRef);
        const comData = comSnap.data();
        // 会社が未承認の場合は、問い合わせNG
        if (comData?.status !== status.approved) {
          setCantInquiry(t('media.unApprovedCompany'));
        } else {
          const q = query(issuesRef, where('companyId', '==', comId));
          const { empty, docs } = await getDocs(q);
          if (empty) {
            setCantInquiry(t('media.noIssue'));
          } else {
            const approvedDocsCount = docs.filter(
              (document) => document.data().status === status.approved
            ).length;
            if (approvedDocsCount === 0) {
              setCantInquiry(t('media.unApprovedIssue'));
            }
          }
        }
      }
    })();
  }, []);

  return (
    <>
      <CustomLoading loading={loading} />
      <Typography component="div" sx={styles.mypageWrapper}>
        <Sidebar screenType={screenType.client} />
        <Typography
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            position: 'static',
            width: '100%'
          }}
        >
          <Typography sx={styles.mypageHeader} component="div">
            <Typography sx={styles.mypageHeaderTitle}>
              {t('mypage.mediaList')}
            </Typography>
          </Typography>
          <MediaSearchCard search={handleSearchInputChange} />
          <Typography
            component="div"
            sx={{
              '@media screen and (max-width:480px)': { ml: '16px' },
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'row',
              ml: '24px'
            }}
          >
            <Button
              sx={[styles.comparisonButton, styles.blackButton]}
              disabled={compareButtonDisabled}
              onClick={() => {
                window.open(
                  `/main/client/mypage/media/compare?mediaIds=${compareMedias.join(
                    ','
                  )}`
                );
              }}
            >
              <Typography component="div" sx={styles.comparisonButtonText}>
                {t('media.mediaComparison')}
              </Typography>
              <OpenInNewOutlinedIcon sx={styles.comparisonButtonIcon} />
            </Button>
            <CustomGuideButton
              title={t('media.mediaComparison')}
              guideText={t('guideText.media.comparison')}
            />
          </Typography>
          {showList && (
            <Typography component="div" sx={{ mb: '40px' }}>
              <InfiniteScroll
                loadMore={() => {
                  void (async () => {
                    await loadMore();
                  })();
                }}
                hasMore={hasMore}
                loader={<div className="loader" key={0}></div>}
              >
                {searchedMedias.length !== 0 ? (
                  <Typography sx={styles.list} component="div">
                    {searchedMedias.map((value, i) => (
                      <Typography key={i} sx={styles.card} component="div">
                        <MediaCard
                          screen={screenType.client}
                          category={value?.category}
                          image={value?.image}
                          title={value?.name}
                          summaryText={value?.overview}
                          industries={value?.industries}
                          funnel={value?.funnel}
                          mediaType={value?.mediaType}
                          target={value?.target}
                          seasonalMenuMaterialText={
                            value?.seasonalMenuMaterialText
                          }
                          demographicsGeneration={value?.demographicsGeneration}
                          demographicsGender={value?.demographicsGender}
                          hasCheck
                          mediaName={value?.name}
                          mediaId={value?.mediaId}
                          checked={compareMedias.includes(value?.mediaId)}
                          onCheckboxChange={handleCheckboxChange}
                          cantInquiry={cantInquiry}
                        />
                      </Typography>
                    ))}
                  </Typography>
                ) : (
                  <NothingGivenList
                    title={t('media.noMedia')}
                    detail={t('media.noSearchMedia')}
                  />
                )}
              </InfiniteScroll>
            </Typography>
          )}
        </Typography>
      </Typography>
      <CustomSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        text={errorText}
      />
    </>
  );
};

export default ClientMypageMedia;
